import React, { useState, useEffect, useCallback } from 'react';
import {
    Building2, Home, DollarSign, ChevronDown, ChevronUp,
    Search, Filter, Calendar, RefreshCw, MessageSquare, Phone,
    User, Clock, CheckCircle, XCircle, Activity, AlertTriangle,
    BarChart3
} from 'lucide-react';
import api from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import PaymentPeriodHistoryModal from '../components/PaymentPeriodHistoryModal';

// ─── Color helpers ───────────────────────────────────────────────────────────
const getBarColor = (rate) => {
    if (rate >= 90) return 'bg-green-500';
    if (rate >= 70) return 'bg-emerald-400';
    if (rate >= 50) return 'bg-yellow-400';
    if (rate >= 30) return 'bg-orange-400';
    return 'bg-red-500';
};

const getStatusDot = (status) => {
    switch (status) {
        case 'paid': return <span className="inline-block h-3 w-3 rounded-full bg-green-500 mr-1.5" />;
        case 'unpaid': return <span className="inline-block h-3 w-3 rounded-full bg-orange-400 mr-1.5" />;
        case 'overdue': return <span className="inline-block h-3 w-3 rounded-full bg-red-500 mr-1.5" />;
        case 'partial': return <span className="inline-block h-3 w-3 rounded-full bg-yellow-400 mr-1.5" />;
        case 'not_due': return <span className="inline-block h-3 w-3 rounded-full bg-blue-400 mr-1.5" />;
        case 'unoccupied': return <span className="inline-block h-3 w-3 rounded-full bg-gray-300 mr-1.5" />;
        default: return <span className="inline-block h-3 w-3 rounded-full bg-gray-300 mr-1.5" />;
    }
};

const getStatusLabel = (status) => {
    switch (status) {
        case 'paid': return 'Paid';
        case 'unpaid': return 'Unpaid';
        case 'overdue': return 'Overdue';
        case 'partial': return 'Partial';
        case 'not_due': return 'Not due yet';
        case 'unoccupied': return 'Unoccupied';
        default: return status;
    }
};

const formatCurrency = (amount) => {
    if (!amount || amount === 0) return 'KES 0';
    return `KES ${parseFloat(amount).toLocaleString()}`;
};

// ─── Billing cycle label helper ────────────────────────────────────────────
const getBillingLabel = (billingCycle) => {
    switch (billingCycle) {
        case 'daily':   return '/day';
        case 'weekly':  return '/wk';
        case 'monthly': return '/mo';
        case 'termly':  return '/term';
        case 'milestone': return ' (one-time)';
        default: return '/mo';
    }
};

// ─── Toast Component ─────────────────────────────────────────────────────────
const Toast = ({ toast, onClose }) => {
    if (!toast) return null;
    const isSuccess = toast.type === 'success';
    useEffect(() => {
        const timer = setTimeout(onClose, 4000);
        return () => clearTimeout(timer);
    }, [toast, onClose]);

    return (
        <div className={`fixed top-4 right-4 z-50 flex items-center space-x-2 px-4 py-3 rounded-lg shadow-lg transition-all ${
            isSuccess ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
            {isSuccess ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
            <p className="text-sm font-medium">{toast.message}</p>
            <button onClick={onClose} className="ml-2 hover:opacity-80">
                <XCircle className="h-4 w-4" />
            </button>
        </div>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const AssetPaymentOverview = () => {
    const now = new Date();
    const [selectedYear, setSelectedYear] = useState(now.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);

    const [summary, setSummary] = useState(null);
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);

    const [expandedAssetId, setExpandedAssetId] = useState(null);
    const [expandedUnits, setExpandedUnits] = useState([]);
    const [unitsLoading, setUnitsLoading] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    // SMS sending state — track which lease is currently sending
    const [sendingSms, setSendingSms] = useState(null);    // leaseId | 'bulk:assetId' | null

    // Period history modal state
    const [historyModal, setHistoryModal] = useState({ open: false, leaseId: null, tenantData: null });
    const [toast, setToast] = useState(null);

    const showToast = (type, message) => setToast({ type, message });
    const closeToast = () => setToast(null);

    // ─── Fetch data ──────────────────────────────────────────────────────────
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = { year: selectedYear, month: selectedMonth };
            const [summaryRes, assetsRes] = await Promise.all([
                api.get('/payments/overview', { params }),
                api.get('/payments/overview/assets', { params })
            ]);
            setSummary(summaryRes.data?.data || null);
            setAssets(assetsRes.data?.data || []);
        } catch (error) {
            console.error('Error fetching payment overview:', error);
        } finally {
            setLoading(false);
        }
    }, [selectedYear, selectedMonth]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // ─── Expand asset ────────────────────────────────────────────────────────
    const toggleAsset = async (assetId) => {
        if (expandedAssetId === assetId) {
            setExpandedAssetId(null);
            setExpandedUnits([]);
            return;
        }
        setExpandedAssetId(assetId);
        setUnitsLoading(true);
        try {
            const params = { year: selectedYear, month: selectedMonth };
            const res = await api.get(`/payments/overview/assets/${assetId}/units`, { params });
            setExpandedUnits(res.data?.data || []);
        } catch (error) {
            console.error('Error fetching units:', error);
            setExpandedUnits([]);
        } finally {
            setUnitsLoading(false);
        }
    };

    // ─── One-click send SMS to a single tenant ─────────────────────────────
    const handleSendSms = async (leaseId, tenantName) => {
        setSendingSms(leaseId);
        try {
            await api.post(`/sms/late-payment/${leaseId}`);
            showToast('success', `Reminder sent to ${tenantName}`);
        } catch (error) {
            showToast('error', error.response?.data?.error || 'Failed to send SMS');
        } finally {
            setSendingSms(null);
        }
    };

    // ─── One-click send bulk SMS for an asset ──────────────────────────────
    const openHistoryModal = (leaseId, tenantData) => {
        setHistoryModal({ open: true, leaseId, tenantData });
    };

    const closeHistoryModal = () => {
        setHistoryModal({ open: false, leaseId: null, tenantData: null });
    };

    const handleBulkSms = async (assetId, assetName) => {
        const bulkKey = `bulk:${assetId}`;
        setSendingSms(bulkKey);
        try {
            const params = { year: selectedYear, month: selectedMonth };
            const res = await api.get(`/payments/overview/assets/${assetId}/units`, { params });
            const unpaidUnits = (res.data?.data || []).filter(u =>
                (u.payment_status === 'unpaid' || u.payment_status === 'overdue' || u.payment_status === 'partial') && u.lease_id
            );

            if (unpaidUnits.length === 0) {
                showToast('error', 'No unpaid units found.');
                setSendingSms(null);
                return;
            }

            const recipients = unpaidUnits
                .filter(u => u.tenant && u.tenant.phone)
                .map(u => ({ leaseId: u.lease_id, name: `${u.tenant.first_name} ${u.tenant.last_name}` }));

            if (recipients.length === 0) {
                showToast('error', 'No phone numbers available.');
                setSendingSms(null);
                return;
            }

            // Send sequentially
            let sent = 0;
            for (const r of recipients) {
                try {
                    await api.post(`/sms/late-payment/${r.leaseId}`);
                    sent++;
                } catch (e) {
                    // skip failed ones
                }
            }

            showToast('success', `Sent ${sent}/${recipients.length} reminders for ${assetName}`);
        } catch (error) {
            showToast('error', error.response?.data?.error || 'Failed to send bulk reminders');
        } finally {
            setSendingSms(null);
        }
    };

    // ─── Filters ─────────────────────────────────────────────────────────────
    const filteredAssets = assets.filter(a => {
        const matchesSearch = a.asset_name?.toLowerCase().includes(searchTerm.toLowerCase());
        let matchesStatus = true;
        if (statusFilter === 'good') matchesStatus = a.collection_rate >= 90;
        else if (statusFilter === 'warning') matchesStatus = a.collection_rate >= 50 && a.collection_rate < 90;
        else if (statusFilter === 'bad') matchesStatus = a.collection_rate < 50;
        return matchesSearch && matchesStatus;
    });

    const months = [
        { value: 1, label: 'January' }, { value: 2, label: 'February' },
        { value: 3, label: 'March' }, { value: 4, label: 'April' },
        { value: 5, label: 'May' }, { value: 6, label: 'June' },
        { value: 7, label: 'July' }, { value: 8, label: 'August' },
        { value: 9, label: 'September' }, { value: 10, label: 'October' },
        { value: 11, label: 'November' }, { value: 12, label: 'December' },
    ];
    const years = [];
    for (let y = now.getFullYear() - 2; y <= now.getFullYear() + 1; y++) years.push(y);

    // ─── Loading ─────────────────────────────────────────────────────────────
    if (loading && !summary) return <LoadingSpinner size="lg" />;

    return (
        <div className="space-y-6">
            {/* ── Toast notification ── */}
            <Toast toast={toast} onClose={closeToast} />

            {/* ── Header ── */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Asset Payment Overview</h1>
                    <p className="text-gray-600">Visual payment status across all your properties</p>
                </div>
                <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2 bg-white rounded-lg border border-gray-300 p-1">
                        <Calendar className="h-4 w-4 text-gray-500 ml-1" />
                        <select
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                            className="text-sm border-0 focus:ring-0 bg-transparent"
                        >
                            {months.map(m => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                        </select>
                        <select
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                            className="text-sm border-0 focus:ring-0 bg-transparent"
                        >
                            {years.map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>
                    <button
                        onClick={fetchData}
                        className="flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        <RefreshCw className="h-4 w-4 mr-1" /> Refresh
                    </button>
                </div>
            </div>

            {/* ── Summary Cards ── */}
            {summary && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Total Assets</p>
                                <p className="text-2xl font-bold text-gray-900">{summary.total_assets}</p>
                            </div>
                            <Building2 className="h-8 w-8 text-indigo-500" />
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Total Units</p>
                                <p className="text-2xl font-bold text-gray-900">{summary.total_units}</p>
                                <p className="text-xs text-gray-500">
                                    {summary.occupied_units} occupied &middot; {summary.total_units - summary.occupied_units} vacant
                                </p>
                            </div>
                            <Home className="h-8 w-8 text-blue-500" />
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Collection Rate</p>
                                <p className={`text-2xl font-bold ${
                                    summary.collection_rate >= 90 ? 'text-green-600' :
                                    summary.collection_rate >= 50 ? 'text-yellow-600' : 'text-red-600'
                                }`}>{summary.collection_rate}%</p>
                                <p className="text-xs text-gray-500">{summary.paid_units} of {summary.occupied_units} paid</p>
                            </div>
                            <Activity className={`h-8 w-8 ${
                                summary.collection_rate >= 90 ? 'text-green-500' :
                                summary.collection_rate >= 50 ? 'text-yellow-500' : 'text-red-500'
                            }`} />
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Revenue Collected</p>
                                <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.total_collected)}</p>
                                <p className="text-xs text-gray-500">of {formatCurrency(summary.total_expected)} expected</p>
                            </div>
                            <DollarSign className="h-8 w-8 text-green-500" />
                        </div>
                    </div>
                </div>
            )}

            {/* ── Filters ── */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="relative flex-1 max-w-xs">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search assets..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                </div>
                <div className="flex items-center space-x-2">
                    <Filter className="h-4 w-4 text-gray-500" />
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="text-sm border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500"
                    >
                        <option value="all">All Status</option>
                        <option value="good">Good ({'\u2265'}90%)</option>
                        <option value="warning">Warning (50-89%)</option>
                        <option value="bad">{'Bad (<50%)'}</option>
                    </select>
                </div>
            </div>

            {/* ── Asset Cards ── */}
            {filteredAssets.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg shadow">
                    <Building2 className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p className="text-gray-500">No assets found</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {filteredAssets.map((asset) => {
                        const isExpanded = expandedAssetId === asset.asset_id;
                        const rate = asset.collection_rate || 0;
                        const unpaidCount = asset.leased_units - asset.paid_units;
                        const isSendingBulk = sendingSms === `bulk:${asset.asset_id}`;
                        const assetBillingLabel = getBillingLabel(asset.billing_cycle);

                        return (
                            <div key={asset.asset_id} className="bg-white rounded-lg shadow overflow-hidden">
                                {/* Asset card header (clickable) */}
                                <button
                                    onClick={() => toggleAsset(asset.asset_id)}
                                    className="w-full text-left p-4 hover:bg-gray-50 transition-colors"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-3 min-w-0">
                                            <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                                                {asset.category === 'property' || asset.category === 'building' ? (
                                                    <Building2 className="h-5 w-5 text-indigo-600" />
                                                ) : (
                                                    <Home className="h-5 w-5 text-indigo-600" />
                                                )}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="flex items-center space-x-2">
                                                    <h3 className="text-sm font-semibold text-gray-900 truncate">{asset.asset_name}</h3>
                                                    <span className="text-xs text-gray-500 capitalize bg-gray-100 px-2 py-0.5 rounded">{asset.category}</span>
                                                    {unpaidCount > 0 && (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                                            {unpaidCount} pending
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-gray-500 mt-0.5">
                                                    {asset.leased_units} leased &middot; {asset.total_units} total &middot; {formatCurrency(asset.expected_rent)}{assetBillingLabel}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-center space-x-4 flex-shrink-0">
                                            {/* Progress bar (desktop) */}
                                            <div className="hidden sm:block w-48">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-xs font-medium text-gray-700">{rate}%</span>
                                                    <span className="text-xs text-gray-500">{asset.paid_units}/{asset.leased_units} paid</span>
                                                </div>
                                                <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                                                    {rate > 0 && (
                                                        <div className={`h-full rounded-full transition-all duration-500 ${getBarColor(rate)}`} style={{ width: `${rate}%` }} />
                                                    )}
                                                    {rate === 0 && asset.leased_units > 0 && (
                                                        <div className="h-full w-full bg-red-500 rounded-full" />
                                                    )}
                                                </div>
                                            </div>
                                            {isExpanded ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                                        </div>
                                    </div>

                                    {/* Progress bar (mobile) */}
                                    <div className="sm:hidden mt-3">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-xs font-medium text-gray-700">{rate}%</span>
                                            <span className="text-xs text-gray-500">{asset.paid_units}/{asset.leased_units} paid</span>
                                        </div>
                                        <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                                            <div className={`h-full rounded-full transition-all duration-500 ${getBarColor(rate)}`} style={{ width: `${rate}%` }} />
                                        </div>
                                    </div>

                                    {/* Bulk Send button (one-click) */}
                                    {unpaidCount > 0 && (
                                        <div className="mt-2 flex justify-end">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleBulkSms(asset.asset_id, asset.asset_name); }}
                                                disabled={isSendingBulk}
                                                className="flex items-center px-3 py-1.5 text-xs font-medium bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                <MessageSquare className="h-3.5 w-3.5 mr-1" />
                                                {isSendingBulk ? 'Sending...' : `Send All Reminders (${unpaidCount})`}
                                            </button>
                                        </div>
                                    )}
                                </button>

                                {/* Expanded units section */}
                                {isExpanded && (
                                    <div className="border-t border-gray-200">
                                        {unitsLoading ? (
                                            <div className="flex justify-center py-8"><LoadingSpinner size="sm" /></div>
                                        ) : expandedUnits.length === 0 ? (
                                            <div className="text-center py-6 text-gray-500">
                                                <Home className="h-8 w-8 mx-auto mb-1 text-gray-300" />
                                                <p className="text-sm">No units found for this asset</p>
                                            </div>
                                        ) : (
                                            <div className="divide-y divide-gray-100">
                                                {expandedUnits.map((unit) => {
                                                    const isOverdue = unit.payment_status === 'overdue';
                                                    const isUnpaid = unit.payment_status === 'unpaid';
                                                    const isPartial = unit.payment_status === 'partial';
                                                    const isNotDue = unit.payment_status === 'not_due';
                                                    const isPaid = unit.payment_status === 'paid';
                                                    const isUnoccupied = unit.payment_status === 'unoccupied';
                                                    const isRemindable = (isOverdue || isUnpaid || isPartial) && unit.lease_id;
                                                    const hasArrears = unit.periods_overdue > 0;
                                                    const isSendingThis = sendingSms === unit.lease_id;
                                                    const outstandingAmount = unit.rent_amount - (unit.payment?.amount || 0);
                                                    const unitBillingLabel = getBillingLabel(unit.billing_cycle);

                                                    return (
                                                        <div key={unit.unit_id} className={`p-4 ${
                                                            isOverdue ? 'bg-red-50 border-l-4 border-l-red-500' :
                                                            isUnpaid ? 'bg-orange-50 border-l-4 border-l-orange-400' :
                                                            isPartial ? 'bg-yellow-50 border-l-4 border-l-yellow-400' :
                                                            isNotDue ? 'bg-blue-50 border-l-4 border-l-blue-400' :
                                                            isPaid ? 'bg-white border-l-4 border-l-green-500' :
                                                            'bg-gray-50 border-l-4 border-l-gray-300'
                                                        }`}>
                                                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                                                <div className="flex items-start space-x-3 min-w-0">
                                                                    {getStatusDot(unit.payment_status)}
                                                                    <div className="min-w-0">
                                                                        <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                                                                            <span className="text-sm font-medium text-gray-900">
                                                                                {unit.unit_number || unit.unit_name || `Unit #${unit.unit_id}`}
                                                                            </span>
                                                                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                                                                isOverdue ? 'bg-red-100 text-red-700' :
                                                                                isUnpaid ? 'bg-orange-100 text-orange-700' :
                                                                                isPartial ? 'bg-yellow-100 text-yellow-700' :
                                                                                isNotDue ? 'bg-blue-100 text-blue-700' :
                                                                                isPaid ? 'bg-green-100 text-green-700' :
                                                                                'bg-gray-200 text-gray-600'
                                                                            }`}>
                                                                                {getStatusLabel(unit.payment_status)}
                                                                            </span>
                                                                            {isPartial && (
                                                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                                                                                    KES {outstandingAmount.toLocaleString()} remaining
                                                                                </span>
                                                                            )}
                                                                            {hasArrears && (
                                                                                <>
                                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-200 text-red-800">
                                                                                        {unit.periods_overdue} period{unit.periods_overdue > 1 ? 's' : ''} overdue
                                                                                    </span>
                                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                                                                        Arrears: {formatCurrency(unit.total_arrears)}
                                                                                    </span>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                        {unit.tenant ? (
                                                                            <div className="flex items-center space-x-2 mt-0.5 text-xs text-gray-600">
                                                                                <User className="h-3 w-3" />
                                                                                <span>{unit.tenant.first_name} {unit.tenant.last_name}</span>
                                                                                {unit.tenant.phone && (
                                                                                    <>
                                                                                        <span>&middot;</span>
                                                                                        <Phone className="h-3 w-3" />
                                                                                        <span>{unit.tenant.phone}</span>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        ) : (
                                                                            <p className="text-xs text-gray-400 mt-0.5">No tenant assigned</p>
                                                                        )}
                                                                        <div className="flex items-center space-x-3 mt-0.5 text-xs text-gray-500">
                                                                            <span>Rent: {formatCurrency(unit.rent_amount)}{unitBillingLabel}</span>
                                                                            {unit.payment_due_day && <span>Due day: {unit.payment_due_day}</span>}
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* One-click Send button for unpaid/overdue/partial */}
                                                                {(isOverdue || isUnpaid || isPartial) && unit.lease_id && (
                                                                    <div className="flex items-center flex-shrink-0 pl-6 sm:pl-0">
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleSendSms(unit.lease_id, `${unit.tenant?.first_name || ''} ${unit.tenant?.last_name || ''}`);
                                                                            }}
                                                                            disabled={isSendingThis}
                                                                            className={`flex items-center px-3 py-1.5 text-xs font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                                                                                isPartial
                                                                                    ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                                                                                    : 'bg-orange-500 text-white hover:bg-orange-600'
                                                                            }`}
                                                                        >
                                                                            <MessageSquare className="h-3.5 w-3.5 mr-1" />
                                                                            {isSendingThis ? 'Sending...' : isPartial ? 'Send Balance Reminder' : 'Send Reminder'}
                                                                        </button>
                                                                    </div>
                                                                )}

                                                                {/* View History button - for any occupied unit */}
                                                                {unit.lease_id && (
                                                                    <div className="flex items-center flex-shrink-0 pl-6 sm:pl-0">
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                openHistoryModal(unit.lease_id, unit.tenant || null);
                                                                            }}
                                                                            className="flex items-center px-3 py-1.5 text-xs font-medium rounded-lg bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-colors"
                                                                        >
                                                                            <BarChart3 className="h-3.5 w-3.5 mr-1" />
                                                                            View History
                                                                        </button>
                                                                    </div>
                                                                )}

                                                                {isPaid && unit.payment && (
                                                                    <div className="text-xs text-green-700 flex items-center flex-shrink-0 pl-6 sm:pl-0">
                                                                        <CheckCircle className="h-3.5 w-3.5 mr-1" />
                                                                        {formatCurrency(unit.payment.amount)} &middot; {unit.payment.method || 'N/A'}
                                                                    </div>
                                                                )}

                                                                {isUnoccupied && (
                                                                    <div className="text-xs text-gray-400 flex items-center flex-shrink-0 pl-6 sm:pl-0">
                                                                        <Clock className="h-3.5 w-3.5 mr-1" />
                                                                        Available for lease
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ── Payment Period History Modal ── */}
            <PaymentPeriodHistoryModal
                isOpen={historyModal.open}
                onClose={closeHistoryModal}
                leaseId={historyModal.leaseId}
                tenantData={historyModal.tenantData}
            />
        </div>
    );
};

export default AssetPaymentOverview;
